<script setup>
// Solar-Angebot-Vergleich site footer. Renders on every SAV page (homepage
// + Impressum/Datenschutz/AGB/Widerruf + Kontakt + future sub-pages) via
// SolarangebotvergleichChrome. Three-column layout on desktop, stacked on
// mobile. Mirrors solarvergleich.de's footer structure (brand block + quick
// nav + contact column + bottom legal bar).
defineProps({
    brandName: { type: String, default: 'Solar-Angebot-Vergleich' },
    author: { type: Object, default: null },
});

const year = new Date().getFullYear();
</script>

<template>
    <footer class="savf">
        <div class="savf__inner">
            <div class="savf__col savf__col--brand">
                <a class="savf__brand" href="/" :aria-label="brandName">
                    <svg class="savf__sun" viewBox="0 0 40 40" width="40" height="40" aria-hidden="true" style="width:40px;height:40px;flex-shrink:0;">
                        <circle cx="20" cy="20" r="7.5" fill="var(--sav-sun, #FACC15)" />
                        <g stroke="var(--sav-sun, #FACC15)" stroke-width="2.4" stroke-linecap="round">
                            <line x1="20" y1="3" x2="20" y2="8" />
                            <line x1="20" y1="32" x2="20" y2="37" />
                            <line x1="3" y1="20" x2="8" y2="20" />
                            <line x1="32" y1="20" x2="37" y2="20" />
                            <line x1="7.5" y1="7.5" x2="11" y2="11" />
                            <line x1="29" y1="29" x2="32.5" y2="32.5" />
                            <line x1="32.5" y1="7.5" x2="29" y2="11" />
                            <line x1="11" y1="29" x2="7.5" y2="32.5" />
                        </g>
                    </svg>
                    <span class="savf__wordmark">
                        Solar<span class="savf__dash">-</span>Angebot<span class="savf__dash">-</span>Vergleich
                    </span>
                </a>
                <p class="savf__tagline">Photovoltaik-Angebote ehrlich vergleichen. Bis zu drei Angebote regionaler Fachbetriebe. Wir finanzieren uns über eine Vermittlungsvergütung der Fachbetriebe.</p>
                <p class="savf__operator">Ein Service der Science Works - Informations- und Kommunikationssysteme UG (haftungsbeschränkt), Hannover.</p>
            </div>

            <div class="savf__col">
                <h3 class="savf__h">Vergleich</h3>
                <ul class="savf__list">
                    <li><a href="/#sav-vorteile">Vorteile</a></li>
                    <li><a href="/#sav-ablauf">Ablauf</a></li>
                    <li><a href="/#sav-foerderung">Förderung</a></li>
                    <li><a href="/#sav-anfrage">Anfrage starten</a></li>
                    <li><a href="/ratgeber">Ratgeber &amp; Blog</a></li>
                </ul>
            </div>

            <div class="savf__col">
                <h3 class="savf__h">Hilfe &amp; Kontakt</h3>
                <ul class="savf__list">
                    <li><a href="/kontakt">Kontakt</a></li>
                    <li><a href="mailto:hilfe@solar-angebot-vergleich.de">hilfe@solar-angebot-vergleich.de</a></li>
                    <li><a href="/widerruf">Widerruf / Datenlöschung</a></li>
                </ul>
            </div>

            <div class="savf__col">
                <h3 class="savf__h">Rechtliches</h3>
                <ul class="savf__list">
                    <li><a href="/impressum">Impressum</a></li>
                    <li><a href="/datenschutz">Datenschutz</a></li>
                    <li><a href="/agb">AGB</a></li>
                    <li><a href="/widerruf">Widerruf</a></li>
                </ul>
            </div>
        </div>

        <div class="savf__bar">
            <div class="savf__barInner">
                <p>&copy; {{ year }} {{ brandName }}. Alle Rechte vorbehalten.</p>
                <p v-if="author">Methodik geprüft von <strong>{{ author.name }}</strong><span v-if="author.role"> - {{ author.role }}</span></p>
                <p v-else>
                    Ertragsdaten:
                    <a href="https://re.jrc.ec.europa.eu/pvg_tools/en/" rel="nofollow noopener" target="_blank" class="savf__cite">PVGIS v5.3 (JRC)</a>.
                    Preisbenchmarks orientiert am
                    <a href="https://www.solarwirtschaft.de/datawall/uploads/2025/12/BSW_Preismonitor_Photovoltaik_Q4_2025.pdf" rel="nofollow noopener" target="_blank" class="savf__cite">BSW-Solar Preismonitor Q4 2025</a>.
                </p>
            </div>
        </div>
    </footer>
</template>

<style scoped>
.savf {
    background: var(--sav-notice-bg, #0F1A35);
    color: #E5E0CC;
    margin-top: 64px;
}
.savf__inner {
    max-width: 1240px;
    margin: 0 auto;
    padding: 56px 24px 36px;
    display: grid;
    grid-template-columns: 2fr 1fr 1fr 1fr;
    gap: 40px;
}
.savf__col h3, .savf__h {
    font-size: 14px;
    margin: 0 0 16px;
    font-weight: 700;
    color: var(--sav-sun, #FACC15);
    text-transform: uppercase;
    letter-spacing: 0.08em;
}
.savf__col--brand .savf__brand {
    display: inline-flex;
    align-items: center;
    gap: 12px;
    text-decoration: none;
    color: #fff;
    margin-bottom: 14px;
}
.savf__wordmark {
    font-weight: 800;
    font-size: 18px;
    letter-spacing: -0.012em;
}
.savf__dash {
    color: var(--sav-sun, #FACC15);
    font-weight: 800;
    padding: 0 1px;
}
.savf__tagline {
    color: #BFC7D8;
    font-size: 14px;
    line-height: 1.55;
    margin: 0 0 12px;
    max-width: 44ch;
}
.savf__operator {
    color: #8A93AC;
    font-size: 12px;
    line-height: 1.5;
    margin: 0;
    max-width: 44ch;
}
.savf__list {
    list-style: none;
    padding: 0;
    margin: 0;
    display: flex;
    flex-direction: column;
    gap: 10px;
}
.savf__list a {
    color: #E5E0CC;
    text-decoration: none;
    font-size: 15px;
    line-height: 1.4;
    transition: color 0.15s ease;
}
.savf__list a:hover {
    color: var(--sav-sun, #FACC15);
    text-decoration: underline;
    text-underline-offset: 3px;
}
.savf__bar {
    border-top: 1px solid rgba(255,255,255,0.10);
    background: rgba(0,0,0,0.20);
}
.savf__barInner {
    max-width: 1240px;
    margin: 0 auto;
    padding: 18px 24px;
    display: flex;
    flex-wrap: wrap;
    justify-content: space-between;
    gap: 12px;
    color: #8A93AC;
    font-size: 12.5px;
}
.savf__barInner p { margin: 0; }
.savf__barInner strong { color: var(--sav-sun, #FACC15); font-weight: 700; }
.savf__cite { color: var(--sav-sun, #FACC15); text-decoration: none; }
.savf__cite:hover { text-decoration: underline; text-underline-offset: 2px; }
@media (max-width: 920px) {
    .savf__inner { grid-template-columns: 1fr 1fr; gap: 32px 28px; padding: 44px 24px 28px; }
    .savf__col--brand { grid-column: 1 / -1; }
}
@media (max-width: 540px) {
    .savf__inner { grid-template-columns: 1fr; }
}
</style>
